import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mattersApi, clientsApi } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Search, FolderKanban, ArrowRight, Clock, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const TURTLE_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/r0l0976o_Transparent%20BG%20Turtle.png';

const Matters = () => {
  const [matters, setMatters] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [mattersRes, clientsRes] = await Promise.all([
          mattersApi.getAll(),
          clientsApi.getAll()
        ]);
        setMatters(mattersRes.data);
        setClients(clientsRes.data);
      } catch (error) {
        toast.error('Failed to fetch matters');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredMatters = matters.filter(matter =>
    matter.title?.toLowerCase().includes(search.toLowerCase()) ||
    matter.description?.toLowerCase().includes(search.toLowerCase())
  );

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Unknown Client';
  };

  const getStageColor = (stage) => {
    switch (stage) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'active': return 'bg-blue-100 text-blue-700';
      case 'review': return 'bg-purple-100 text-purple-700';
      case 'planning': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="matters-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2B5E] font-['Amaranth']">Matters</h1>
          <p className="text-navy/60 mt-1">All projects and workstreams across clients</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
        <Input
          placeholder="Search matters..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="matter-search-input"
        />
      </div>

      {/* Matters grid */}
      {filteredMatters.length === 0 ? (
        <div className="empty-state py-16">
          <img src={TURTLE_MASCOT} alt="Turtle mascot" className="w-32 h-32 mb-4" />
          <h3 className="text-xl font-['Amaranth'] text-[#1A2B5E] mb-2">No matters found</h3>
          <p className="text-navy/60 mb-4">
            {search ? 'Try a different search term' : 'Create matters from client workspaces'}
          </p>
          <Link to="/clients">
            <Button variant="outline">
              View Clients
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMatters.map((matter) => (
            <Link key={matter.id} to={`/matters/${matter.id}`}>
              <Card className="bg-white border-navy/10 card-hover h-full" data-testid={`matter-card-${matter.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-2">
                    <div className="w-10 h-10 rounded-lg bg-[#40CFDE]/10 flex items-center justify-center shrink-0">
                      <FolderKanban className="w-5 h-5 text-[#40CFDE]" />
                    </div>
                    <Badge className={getStageColor(matter.stage)}>
                      {matter.stage}
                    </Badge>
                  </div>
                  
                  <h3 className="font-semibold text-[#1A2B5E] mt-3">{matter.title}</h3>
                  <p className="text-sm text-navy/60 mt-1">{getClientName(matter.client_id)}</p>
                  
                  {matter.description && (
                    <p className="text-sm text-navy/50 mt-2 line-clamp-2">{matter.description}</p>
                  )}
                  
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Badge className={getPriorityColor(matter.priority)} variant="outline">
                      {matter.priority} priority
                    </Badge>
                    <Badge className={getRiskColor(matter.risk_level)} variant="outline">
                      {matter.risk_level} risk
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-navy/5 text-xs text-navy/50">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {matter.estimated_hours || 0}h est.
                    </span>
                    {matter.due_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(matter.due_date).toLocaleDateString()}
                      </span>
                    )}
                    <span className="text-[#106EFD] flex items-center gap-1">
                      View <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Matters;
